import moment from 'moment';

export const isValidEmailDomain = (email, domainList) => {
  const lowerEmail = email?.toLowerCase() || '';
  return domainList.some((domain) => lowerEmail.endsWith(domain.toLowerCase()));
};

export const showGlobalLoader = () => {
  const loader = document.createElement('div');
  loader.id = 'global-loader';
  loader.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    font-size: 20px;
    color: #333;
  `;
  loader.innerText = 'Redirecting...';
  document.body.appendChild(loader);
};

// The same date reaches this helper in three shapes depending on the caller: the list screens
// pass their display format (DD-MM-YYYY), the adhoc forms pass whatever the native
// <input type='date'> holds (YYYY-MM-DD), and an adhoc form in edit mode passes the stored
// request's date straight through (an ISO timestamp). Accepting all three keeps one
// implementation of "is this slot already in the past" rather than a near-identical helper per
// caller, and keeps the format out of the call sites. The three are unambiguous under strict
// parsing because the leading token differs in width.
const DAY_FORMATS = ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601];
const CLOCK_FORMATS = ['HH:mm', 'HH:mm:ss'];

export const isPastDatetime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return false;

  const day = moment(dateStr, DAY_FORMATS, true);
  const clock = moment(timeStr, CLOCK_FORMATS, true);
  // An unparseable value is not reported as past — the field's own `required`/format validation
  // owns that case, and "already passed" would be a misleading message for it.
  if (!day.isValid() || !clock.isValid()) return false;

  const combined = day
    .clone()
    .startOf('day')
    .hour(clock.hour())
    .minute(clock.minute());
  return combined.isBefore(moment());
};

export function isOutsideRadius(
  officeLocation,
  pickupLocation,
  serviceableRadius
) {
  if (!officeLocation || !pickupLocation) return false;

  const toRad = (value) => (value * Math.PI) / 180;

  const R = 6371; // Earth radius in KM

  const lat1 = Number(officeLocation.latitude);
  const lon1 = Number(officeLocation.longitude);

  const lat2 = Number(pickupLocation.latitude);
  const lon2 = Number(pickupLocation.longitude);

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // distance in KM

  return distance > serviceableRadius;
}

// export function isOutsideRadiusGoogle(officeLocation, pickupLocation, serviceableRadius) {
//   if (!officeLocation || !pickupLocation) return false;

//   const officeLatLng = new google.maps.LatLng(
//     Number(officeLocation.latitude),
//     Number(officeLocation.longitude)
//   );

//   const pickupLatLng = new google.maps.LatLng(
//     Number(pickupLocation.latitude),
//     Number(pickupLocation.longitude)
//   );

//   // Distance in METERS from Google Maps
//   const distanceInMeters = google.maps.geometry.spherical.computeDistanceBetween(
//     officeLatLng,
//     pickupLatLng
//   );

//   const distanceInKM = distanceInMeters / 1000;

//   console.log('Distance in KM:', distanceInKM);

//   return distanceInKM > serviceableRadius;
// }

export const generateTargetDates = (
  fromDate,
  toDate,
  weekOff = [],
  holidayList = []
) => {
  const weekMap = {
    SUN: 0,
    MON: 1,
    TUE: 2,
    WED: 3,
    THU: 4,
    FRI: 5,
    SAT: 6,
  };

  const weekOffNumbers = weekOff.map((day) => weekMap[day]);
  const holidays = new Set(holidayList);

  const start = new Date(fromDate);
  const end = new Date(toDate);

  const result = [];

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().slice(0, 10); // YYYY-MM-DD format

    // skip week-off days
    if (weekOffNumbers.includes(d.getDay())) continue;

    // skip holidays
    if (holidays.has(iso)) continue;

    result.push(iso);
  }

  return result;
};

export const getFormData = (object) =>
  Object.keys(object).reduce((formData, key) => {
    formData.append(key, object[key]);
    return formData;
  }, new FormData());

/**
 * Displays an employee's per-corporate Unique ID.
 *
 * Stored as a number; shown zero-padded to at least two digits, so a corporate's
 * list reads 01, 02 ... 09, 10 rather than 1, 2 ... 9, 10. Numbers of three or
 * more digits are left alone.
 *
 * Shared deliberately: the Unique ID appears in three employee-listing column
 * definitions and in the bulk edit preview, and those four must not drift into
 * showing the same employee as `7` in one place and `07` in another.
 *
 * An employee with no ID yet - one that the backfill scheduler has not reached -
 * renders as an em dash rather than `null` or `0`.
 */
export const padUniqueId = (uniqueId) => {
  if (uniqueId === null || uniqueId === undefined || uniqueId === '') return '—';
  return String(uniqueId).padStart(2, '0');
};

// Labels match the option titles on the adhoc form, so a listing names the pickup the same way
// the requester saw it when they chose it.
const PICKUP_TYPE_LABELS = {
  HOME: 'Home Location',
  NODAL: 'Nodal Point',
  CUSTOM: 'Custom Location',
};

// The pickup option a stored adhoc request was raised with. `pickupType` is only written by the
// v2 save, so a request raised through v1 is interpreted through `isManual` - exactly how the
// form's own `storedPickupType` reads it, which keeps the listing and the form from disagreeing
// about the same request. An unrecognised value is shown as-is rather than mislabelled.
export const pickupTypeLabel = (row) => {
  if (!row) return '';
  const type = row?.pickupType || (row?.isManual == 'YES' ? 'CUSTOM' : 'HOME');
  return PICKUP_TYPE_LABELS[type] ?? type;
};
